import { useState, useRef, useMemo, useCallback, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataGrid, {
  Column,
  Paging,
  Pager,
  Toolbar,
  Item,
  SearchPanel,
  FilterRow,
  Sorting
} from 'devextreme-react/data-grid';
import CustomStore from "devextreme/data/custom_store";
import { ContextMenu } from 'devextreme-react/context-menu';
import api from '../../services/api';
import DocumentSelector from "../../components/DocumentSelector";
import { InputDialog } from '../../components/InputDialog';
import { exportToExcel } from "../../utils/exportUtils";
import 'devextreme/dist/css/dx.material.blue.light.compact.css';
import EditIcon from '@mui/icons-material/Edit';

const ApplicantList = ({ isInactive = false }) => {
  // State management
  const [isDocumentSelectorOpen, setIsDocumentSelectorOpen] = useState(false);
  const [showInputDialog, setShowInputDialog] = useState(false);

  // Refs
  const gridWrapperRef = useRef(null);
  const contextMenuRef = useRef(null);
  const dataGridRef = useRef(null);
  const focusedRowKeyRef = useRef(null);
  const selectedIdsRef = useRef(new Set());
  const prioritizeSelectedRef = useRef(false); // Only for passport search
  const passportSearchIdsRef = useRef(new Set()); // Only passport search results
  const navigate = useNavigate();

  // Helper to update count display in DOM
  const updateCountDisplay = useCallback((count) => {
    const countElement = document.querySelector('.selection-count-display-inline');
    if (countElement) {
      countElement.textContent = count ?? selectedIdsRef.current.size;
    }
  }, []);

  // Parse DevExtreme filter format to backend format
  const parseDevExtremeFilter = (filter) => {
    if (!filter || !Array.isArray(filter)) return [];
    
    // Simple filter: ["field", "operator", "value"]
    if (filter.length === 3 && typeof filter[0] === 'string') {
      const [field, operator, value] = filter;
      return (value !== null && value !== undefined && value !== '') 
        ? [{ field, operator, value }] 
        : [];
    }
    
    // Complex filter: recursively parse nested arrays
    return filter.flatMap(item => Array.isArray(item) ? parseDevExtremeFilter(item) : []);
  };

  // Main Data Source using CustomStore for server-side operations
  const dataSource = useMemo(() => {
    const formatDateInline = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
      } catch (err) {
        console.error('Error formatting date:', err);
        return '';
      }
    };

    return new CustomStore({
      key: 'id',
      loadMode: 'processed',
      load: async (loadOptions) => {
        console.log('📥 DevExtreme Load Options:', loadOptions);

        try {
          // Build parameters for your API
          const params = {
            skip: loadOptions.skip || 0,
            take: loadOptions.take || 15
          };

          // Handle sorting
          if (loadOptions.sort && loadOptions.sort.length > 0) {
            const sort = loadOptions.sort[0];
            params.sortField = sort.selector;
            params.sortOrder = sort.desc ? 'desc' : 'asc';
          }

          // Handle search (from SearchPanel)
          if (loadOptions.searchValue) {
            params.search = loadOptions.searchValue;
          }

          // Handle filtering (from FilterRow)
          // Convert DevExtreme filter to backend-friendly format
          if (loadOptions.filter) {
            console.log('Filter received:', loadOptions.filter);
            const filters = parseDevExtremeFilter(loadOptions.filter);
            if (filters.length > 0) {
              params.filters = JSON.stringify(filters);
            }
          }

          // Send only passport search IDs for priority sorting
          if (prioritizeSelectedRef.current && passportSearchIdsRef.current.size > 0) {
            params.selectedIds = JSON.stringify(Array.from(passportSearchIdsRef.current));
          }

          console.log('🌐 API Request Params:', params);

          const url = isInactive ? "/Applicants/inactive_paged" : "/Applicants/paged";
          const response = await api.get(url, { params });

          console.log('✅ API Response received');

          if (!response.data) {
            throw new Error('No data received from server');
          }

          // Your API returns { data: [], totalCount: number }
          const items = response.data.data || [];
          const totalCount = response.data.totalCount || 0;

          console.log(`📊 Received ${items.length} items, totalCount: ${totalCount}`);

          // Transform data - format dates and ensure consistent field names
          const transformedData = items.map(item => ({
            ...item,
            applicationDate: formatDateInline(item.applicationDate),
            statusDate: formatDateInline(item.statusDate),
            bioEntryDate: formatDateInline(item.bioEntryDate),
            applicationNo: item.applicationNo || '',
            fullName: item.fullName || '',
            passportNo: item.passportNo || '',
            religion: item.religion || '',
            experienceInAbroad: item.experienceInAbroad || '',
            workingCountry: item.workingCountry || '',
            sponsorName: item.sponsorName || '',
            sponsorID: item.sponsorID || '',
            visaNo: item.visaNo || '',
            agent: item.agent || '',
            brokerName: item.brokerName || '',
            cocCenter: item.cocCenter || '',
            medicalOnlineStatus: item.medicalOnlineStatus || '',
            status: item.status || '',
            laborID: item.laborID || '',
            inMusaned: item.inMusaned || '',
            createdBy: item.createdBy || ''
          }));

          console.log(`✨ Transformed ${transformedData.length} items`);

          return { data: transformedData, totalCount };
        } catch (err) {
          console.error('❌ Data load error:', err);
          return { data: [], totalCount: 0 };
        }
      },
      byKey: () => Promise.resolve(null) // Disable to prevent unnecessary API calls
    });
  }, [isInactive]);



  const handleFocusedRowChanged = useCallback((e) => {
    if (e.row && e.row.key) {
      focusedRowKeyRef.current = e.row.key;
    }
  }, []);

  const handleContextMenuPreparing = useCallback((e) => {
    const shouldShowCustomMenu = (
      e.row?.rowType === "data" && 
      e.row?.data && 
      gridWrapperRef.current?.contains(e.event.target)
    );

    if (shouldShowCustomMenu) {
      e.event.preventDefault();
      focusedRowKeyRef.current = e.row.key;

      const clickX = e.event.pageX;
      const clickY = e.event.pageY;

      setTimeout(() => {
        contextMenuRef.current?.instance.option({
          position: {
            of: document.documentElement,
            my: 'top left',
            at: 'top left',
            offset: `${clickX} ${clickY}`,
            collision: 'fit fit'
          }
        });
        contextMenuRef.current?.instance.show();
      }, 10);
    }
  }, []);

  // Get selected row data
  const getSelectedRowData = useCallback(() => {
    if (!focusedRowKeyRef.current || !dataGridRef.current?.instance) return null;
    
    const visibleRows = dataGridRef.current.instance.getVisibleRows();
    const foundRow = visibleRows.find(row => row.key === focusedRowKeyRef.current);
    return foundRow?.data || null;
  }, []);

  // Unified export handler
  const handleExport = useCallback(async (ids, type) => {
    const exportConfig = {
      tasheer: { endpoint: "/applicants/tasheer", prefix: "Tasheer_Export" },
      selected: { endpoint: "/applicants/export-selected", prefix: "Applicants" },
      all: { endpoint: "/applicants/export-all", prefix: "Applicants" }
    };
    
    const config = exportConfig[type];
    if (!config) return;
    
    try {
      const filename = `${config.prefix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      await exportToExcel(ids, config.endpoint, filename);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed: ' + (err.message || 'Unknown error'));
    }
  }, []);

  // Action handler
  const handleAction = useCallback((type) => {
    const selectedRowData = getSelectedRowData();
    
    const idsArray = Array.from(selectedIdsRef.current);
    const idsToUse = idsArray.length > 0 ? idsArray : (selectedRowData ? [selectedRowData.id] : []);
    
    console.log('Action - IDs to use:', idsToUse);
    
    if (idsToUse.length === 0) return;
    executeAction(type, idsToUse, selectedRowData);
  }, [getSelectedRowData]);

  const executeAction = useCallback((type, idsToUse, selectedRowData) => {
    switch(type) {
      case 'enjaz':
        navigate(`/reports/applicantreport/${idsToUse[0]}`);
        break;
      case 'summary':
        navigate("/reports/applicantSummary", { state: { selectedIds: idsToUse } });
        break;
      case 'cv':
        navigate("/reports/print_cv", { state: { selectedIds: idsToUse } });
        break;
      case 'documents':
        console.log(`Opening document selector with ${idsToUse.length} selected items`);
        setIsDocumentSelectorOpen(true);
        break;
      case 'ticket-request':
        navigate("/reports/ticket-request", { state: { selectedIds: idsToUse } });
        break;
      case 'beneficiary-declaration':
        navigate("/reports/print-benificary", { state: { selectedIds: idsToUse } });
        break;
      case 'commissionInquiry':
        navigate("/reports/commissionInquiry", { state: { selectedIds: idsToUse } });
        break;
      case 'send-applicant':
        navigate(`/applicants/sendApplicant/${selectedRowData.id}`);
        break;
      case 'move-applicants':
        navigate("/applicants/moveApplicants", { state: { selectedIds: idsToUse } });
        break;
      case 'change-status':
        navigate("/applicants/changeStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'change-medical-status':
        navigate("/applicants/medicalStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'change-coc-status':
        navigate("/applicants/cocStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'change-lmis-issue-date':
        navigate("/applicants/changeLmisDate", { state: { selectedIds: idsToUse } });
        break;
      case 'add-complain':
        navigate(`/complains/create/${selectedRowData.id}`);
        break;
      case 'add-flight':
        navigate("/flights/create", { state: { selectedIds: idsToUse } });
        break;
      case 'export-tasheer':
        handleExport(idsToUse, 'tasheer');
        break;
      default:
        break;
    }
  }, [navigate, handleExport]);

  const handleContextMenuClick = useCallback((e) => {
    const selectedRowData = getSelectedRowData();
    if (!selectedRowData) return;

    const idsArray = Array.from(selectedIdsRef.current);
    const idsToUse = idsArray.length > 0 ? idsArray : [selectedRowData.id];
    
    console.log('Context menu - IDs to use:', idsToUse);
    executeContextMenuAction(e.itemData.text, idsToUse, selectedRowData);
  }, [getSelectedRowData]);

  const executeContextMenuAction = useCallback((actionText, idsToUse, selectedRowData) => {
    switch (actionText) {
      case 'Print':
        navigate(`/reports/applicantreport/${selectedRowData.id}`);
        break;
      case 'Print Summary':
        navigate("/reports/applicantSummary", { state: { selectedIds: idsToUse } });
        break;
      case "Check Visa Deligation (Wekala)":
        (async () => {
          try {
            const visaNo = selectedRowData?.visaNo;
            const sponsorId = selectedRowData?.sponsorID;
            const textToCopy = `VNo(${visaNo}),SId(${sponsorId})`;
            await navigator.clipboard.writeText(textToCopy);
            window.open("https://visa.mofa.gov.sa/Enjaz/GetVisaInformation/Person", "_blank");
          } catch (err) {
            console.error('Failed to copy to clipboard:', err);
          }
        })();
        break;
      case 'Export for Tasheer':
        handleExport(idsToUse, 'tasheer');
        break;
      case 'Export Selected Applicants':
        handleExport(idsToUse, 'selected');
        break;
      case 'Export All':
        handleExport([], 'all');
        break;
      case 'Print Contract':
        navigate("/reports/print_contract", { state: { selectedIds: idsToUse } });
        break;
      case 'Print CV':
        navigate("/reports/print_cv", { state: { selectedIds: idsToUse } });
        break;
      case 'Print Selected Documents':
        setIsDocumentSelectorOpen(true);
        break;
      case 'Forensic Request Letter':
        navigate("/reports/forensic-request", { state: { selectedIds: idsToUse } });
        break;
      case 'Ticket Request List':
        navigate("/reports/ticket-request", { state: { selectedIds: idsToUse } });
        break;
      case 'Print Benifecary Declaration':
        navigate("/reports/print-benificary", { state: { selectedIds: idsToUse } });
        break;
      case 'Commission Inquiry':
        navigate("/reports/commissionInquiry", { state: { selectedIds: idsToUse } });
        break;
      case 'Send Applicant':
        navigate(`/applicants/sendApplicant/${selectedRowData.id}`);
        break;
      case 'Move Applicants':
        navigate("/applicants/moveApplicants", { state: { selectedIds: idsToUse } });
        break;
      case 'Change Applicant Status':
        navigate("/applicants/changeStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'Medical Status':
        navigate("/applicants/medicalStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'COC Status':
        navigate("/applicants/cocStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'LMIS Issue Date':
        navigate("/applicants/changeLmisDate", { state: { selectedIds: idsToUse } });
        break;
      case 'Tasheer Status':
        navigate("/applicants/tasheerStatus", { state: { selectedIds: idsToUse } });
        break;
      case 'Add Complain':
        navigate(`/complains/create/${selectedRowData.id}`);
        break;
      case 'Add Flight Detail':
        navigate("/flights/create", { state: { selectedIds: idsToUse } });
        break;
      case 'Search by Passport Numbers':
        handleOpenInputDialog();
        break;
      default:
        break;
    }
  }, [navigate, handleExport]);

  const handleDocumentSelection = useCallback((selectedItems) => {
    let idsToUse = Array.from(selectedIdsRef.current);
    
    // If no items selected, use focused row ID
    if (idsToUse.length === 0 && focusedRowKeyRef.current) {
      const selectedRowData = getSelectedRowData();
      if (selectedRowData?.id) {
        idsToUse = [selectedRowData.id];
      }
    }
    
    setIsDocumentSelectorOpen(false);
    
    navigate("/reports/print_documents", { state: { selectedIds: idsToUse, selectedItems } });
  }, [navigate, getSelectedRowData]);

  const handleOpenInputDialog = () => {
    setShowInputDialog(true);
  };

  const handleInputDialogClose = async (result) => {
    setShowInputDialog(false);
    
    if (result) {
      const passportNumbers = result
        .split(/[\n,\s]+/)
        .map(num => num.trim())
        .filter(num => num.length > 0 && num !== "All");
  
      if (passportNumbers.length === 0) {
        alert('Please enter at least one passport number');
        return;
      }
      
      //console.log(`Searching for ${passportNumbers.length} passport numbers:`, passportNumbers);
      
      try {
        const response = await api.post('/applicants/search-by-passports', {
          passportNumbers: passportNumbers
        });
        
        if (response.data && response.data.length > 0) {
          const matchingIds = response.data.map(item => item.id || item.ID);
          
          //console.log(`Found ${matchingIds.length} matching applicants`);
          
          // ADD to existing selections instead of replacing
          const previousCount = selectedIdsRef.current.size;
          matchingIds.forEach(id => selectedIdsRef.current.add(id));
          const newCount = selectedIdsRef.current.size;
          const addedCount = newCount - previousCount;
          
          // Store only the passport search results for prioritization
          passportSearchIdsRef.current = new Set(matchingIds);
          
          updateCountDisplay(newCount);
          
          // Enable prioritization to show found applicants at top
          prioritizeSelectedRef.current = true;
          
          // Refresh grid to show checkboxes and prioritize selected items
          if (dataGridRef.current?.instance) {
            dataGridRef.current.instance.refresh();
          }
          
          // Navigate to first page and scroll to show selected rows
          setTimeout(() => {
            if (dataGridRef.current?.instance) {
              const grid = dataGridRef.current.instance;
              
              // Go to first page to see selected rows
              grid.pageIndex(0);
              
              // Find first selected row and focus on it
              const visibleRows = grid.getVisibleRows();
              const firstSelectedRow = visibleRows.find(row => 
                row.rowType === 'data' && selectedIdsRef.current.has(row.data.id)
              );
              
              if (firstSelectedRow) {
                grid.option('focusedRowKey', firstSelectedRow.key);
              }
            }
          }, 100);
          
          //console.log(`✓ Added ${addedCount} new applicants (${previousCount} → ${newCount} total selected)`);
          
          //alert(`✓ Found ${matchingIds.length} applicants!\n\nAdded ${addedCount} new selections.\nTotal selected: ${newCount}\n\nNavigated to show selected rows!`);
        } else {
          alert('No matching applicants found');
        }
      } catch (err) {
        console.error('Search failed:', err);
        alert('Search failed: ' + (err.response?.data?.message || err.message || 'Unknown error'));
      }
    }
  };

  // Selection column cell renderer - Memoized to prevent re-renders
  const SelectionColumnCell = memo(({ data }) => {
    const rowId = data.data.id;
    const isSelected = selectedIdsRef.current.has(rowId);
    
    return (
      <div 
        style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}
      >
        <div 
          data-row-id={rowId}
          style={{ 
            width: '20px', 
            height: '20px', 
            border: '2px solid #ccc',
            borderRadius: '3px',
            backgroundColor: isSelected ? '#1976d2' : 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isSelected ? 'white' : 'transparent',
            fontSize: '12px',
            fontWeight: 'bold',
            transition: 'background-color 0.15s ease, color 0.15s ease'
          }}
        >
          ✓
        </div>
      </div>
    );
  });

  // Select All header component - Memoized
  const SelectAllHeader = memo(() => {
    const [allSelected, setAllSelected] = useState(false);
    
    const handleClick = useCallback(() => {
      if (!dataGridRef.current?.instance) return;
      
      const grid = dataGridRef.current.instance;
      const visibleRows = grid.getVisibleRows();
      const visibleDataRows = visibleRows.filter(row => row.rowType === 'data' && row.data);
      
      const newState = !allSelected;
      setAllSelected(newState);
      
      if (newState) {
        // Select all visible rows
        visibleDataRows.forEach(row => {
          selectedIdsRef.current.add(row.data.id);
        });
      } else {
        // Deselect all visible rows
        visibleDataRows.forEach(row => {
          selectedIdsRef.current.delete(row.data.id);
        });
      }
      
      updateCountDisplay();
      
      // Refresh grid to update checkboxes
      grid.refresh();
    }, [allSelected, updateCountDisplay]);
    
    return (
      <div 
        style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}
        onClick={handleClick}
        title={allSelected ? "Deselect all on this page" : "Select all on this page"}
      >
        <div 
          style={{ 
            width: '20px', 
            height: '20px', 
            border: '2px solid #ccc',
            borderRadius: '3px',
            backgroundColor: allSelected ? '#1976d2' : 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: allSelected ? 'white' : 'transparent',
            fontSize: '12px',
            fontWeight: 'bold',
            transition: 'background-color 0.15s ease, color 0.15s ease',
            pointerEvents: 'none'
          }}
        >
          ✓
        </div>
      </div>
    );
  });

  // Column configuration
  const columns = useMemo(() => {
    const baseColumns = [
      { 
        caption: "...", 
        width: 45, 
        cellRender: (data) => (
          <Link to={`/applicants/edit/${data.data.id}`}>
            <EditIcon fontSize="small" />
          </Link>
        )
      },
      { dataField: 'applicationDate', caption: 'Date', width: 80, dataType: "date" },
      { dataField: 'applicationNo', caption: 'App#', dataType: "string", width: 80 },
      { dataField: 'fullName', caption: 'Full Name', dataType: "string", minWidth: 150 },
      { dataField: 'passportNo', caption: 'Passport#', dataType: "string", width: 80 },
      { dataField: 'age', caption: 'Age', width: 60, dataType: "number" },
      { dataField: 'experienceInAbroad', caption: 'Exp', dataType: "number", width: 50 },
      { dataField: 'workingCountry', caption: 'Works In', dataType: "string", width: 70 },
      { dataField: 'sponsorName', caption: 'Sponsor', dataType: "string", minWidth: 100 },
      { dataField: 'sponsorID', caption: 'Sponsor ID', dataType: "string", width: 90 },
      { dataField: 'visaNo', caption: 'Visa No', dataType: "string", width: 90 },
      { dataField: 'agent', caption: 'Agent', dataType: "string", width: 85 },
      { dataField: 'brokerName', caption: '2nd.Cot', dataType: "string", width: 75 },
      { dataField: 'cocCenter', caption: 'COC', dataType: "string", width: 60 },
      { dataField: 'medicalOnlineStatus', caption: 'MED', width: 60 },
      { dataField: 'medicalDays', caption: 'M.Days', dataType: "number", width: 70 },
      { dataField: 'inMusaned', caption: 'TASH', dataType: "string", width: 65 },
      { dataField: 'status', caption: 'Status', dataType: "string", minWidth: 100 },
      { dataField: 'statusDate', caption: 'Status Date', width: 100, dataType: "date" },
      { dataField: 'bioEntryDate', caption: 'LMIS', width: 90, dataType: "date" },
      { dataField: 'laborID', caption: 'Labor ID', dataType: "string", width: 90 },
      { dataField: 'signedDays', caption: 'Days', dataType: "number", width: 65 },
      { dataField: 'createdBy', caption: 'User', dataType: "string", width: 85 },
      { dataField: 'religion', caption: 'Religion', dataType: "string", width: 85 },
    ];

    return baseColumns;
  }, []);

  // Context menu items
  const contextMenuItems = useMemo(() => [
    { text: 'Search by Passport Numbers', icon: 'search' },
    { text: 'Print', icon: 'print' },
    { text: 'Print Summary', icon: 'print' },
    { text: 'Check Visa Deligation (Wekala)', icon: 'send' },
    {
      text: 'Export',
      icon: 'export',
      items: [
        { text: 'Export for Tasheer', icon: 'export' },
        { text: 'Export Selected Applicants', icon: 'export' },
        { text: 'Export All', icon: 'export' }
      ]
    },
    { text: 'Print Contract', icon: 'print' },
    { text: 'Print CV', icon: 'print' },
    { text: 'Print Selected Documents', icon: 'print' },
    { text: 'Forensic Request Letter', icon: 'print' },
    { text: 'Ticket Request List', icon: 'print' },
    { text: 'Print Benifecary Declaration', icon: 'print' },
    { text: 'Commission Inquiry', icon: 'print' },
    { text: 'Change Applicant Status', icon: 'edit' },
    {
      text: 'Change Other Status',
      icon: 'edit',
      items: [
        { text: 'Medical Status', icon: 'medkit' },
        { text: 'COC Status', icon: 'certificate' },
        { text: 'Tasheer Status', icon: 'file' },
        { text: 'LMIS Issue Date', icon: 'calendar' }
      ]
    },
    { text: 'Add Complain', icon: 'add' },
    { text: 'Add Flight Detail', icon: 'add' },
    { text: 'Move Applicants', icon: 'send' },
    { text: 'Send Applicant', icon: 'send' }
  ], []);

  return (
    <>
    <div className="w-100 h-100 bg-white rounded p-1 position-relative" ref={gridWrapperRef}>
      <DataGrid
        ref={dataGridRef}
        dataSource={dataSource}
        keyExpr="id"
        showBorders
        rowAlternationEnabled
        allowColumnResizing={false}
        columnAutoWidth={false}
        columnResizingMode="widget"
        wordWrapEnabled={false}
        focusedRowEnabled
        showColumnLines
        height="100%"
        width="100%"
        onContextMenuPreparing={handleContextMenuPreparing}
        onFocusedRowChanged={handleFocusedRowChanged}
        repaintChangesOnly={true}
        cacheEnabled={true}
        loadPanel={{ enabled: false }}
        //hoverStateEnabled={true}
        remoteOperations={{
          paging: true,
          filtering: true,
          sorting: true,
          grouping: false,
          summary: false
        }}
        renderAsync={true}
        onDataErrorOccurred={(error) => {
          console.error('DataGrid error:', error);
        }}
        onCellClick={(e) => {
          // Handle selection column cell clicks
          if (e.column && e.column.caption === '✓' && e.rowType === 'data') {
            e.event.stopPropagation();
            const rowId = e.data.id;
            
            // Toggle selection
            const isNowSelected = !selectedIdsRef.current.has(rowId);
            if (isNowSelected) {
              selectedIdsRef.current.add(rowId);
            } else {
              selectedIdsRef.current.delete(rowId);
            }
            
            updateCountDisplay();
            
            // Update checkbox UI immediately via DOM (no server call)
            const checkboxElement = e.cellElement.querySelector('[data-row-id]');
            if (checkboxElement) {
              checkboxElement.style.backgroundColor = isNowSelected ? '#1976d2' : 'white';
              checkboxElement.style.color = isNowSelected ? 'white' : 'transparent';
            }
          }
        }}
      >
        <Toolbar>
          <Item location="before">
            <div className="d-flex align-items-center es-label-title" style={{ gap: '10px' }}>
              <strong>{isInactive ? 'INACTIVE APPLICANTS' : 'APPLICANTS'}</strong>
              <span style={{ color: '#1976d2', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                Selected: <span className="selection-count-display-inline">0</span>
                {prioritizeSelectedRef.current && (
                  <button
                    onClick={() => {
                      prioritizeSelectedRef.current = false;
                      passportSearchIdsRef.current.clear();
                      if (dataGridRef.current?.instance) {
                        dataGridRef.current.instance.refresh();
                      }
                    }}
                    style={{
                      background: 'none',
                      border: '1px solid #1976d2',
                      borderRadius: '3px',
                      color: '#1976d2',
                      fontSize: '11px',
                      padding: '2px 6px',
                      cursor: 'pointer'
                    }}
                    title="Reset to normal order"
                  >
                    Reset
                  </button>
                )}
              </span>
            </div>
          </Item>

          <Item 
            location="before"
            widget="dxDropDownButton"
            locateInMenu="auto"
            options={{
              text: 'Actions',
              icon: 'menu',
              items: [
                { text: 'Print Enjaz', onClick: () => handleAction('enjaz') },
                { text: 'Print Summary', onClick: () => handleAction('summary') },
                { text: 'Print CV', onClick: () => handleAction('cv') },
                { text: 'Print Documents', onClick: () => handleAction('documents') },
                { text: 'Ticket Request List', onClick: () => handleAction('ticket-request') },
                { text: 'Print Beneficiary', onClick: () => handleAction('beneficiary-declaration') },
                { text: 'Export for Tasheer', onClick: () => handleAction('export-tasheer') },
                { text: 'Commission Inquiry', onClick: () => handleAction('commissionInquiry') }
              ],
              dropDownOptions: { width: 220 },
              stylingMode: 'text'
            }}
          />

          <Item 
            location="before"
            widget="dxDropDownButton"
            locateInMenu="auto"
            options={{
              text: 'Status',
              icon: 'menu',
              items: [
                { text: 'Change Applicant Status', onClick: () => handleAction('change-status') },
                { text: 'Change Medical Status', onClick: () => handleAction('change-medical-status') },
                { text: 'Change COC Status', onClick: () => handleAction('change-coc-status') },
                { text: 'Change LMIS Issue Date', onClick: () => handleAction('change-lmis-issue-date') },
                { text: 'Change Tasheer Status', onClick: () => handleAction('change-musaned-status') }
              ],
              dropDownOptions: { width: 220 },
              stylingMode: 'text'
            }}
          />

          <Item location="before" locateInMenu="auto">
            <button
              className="es-toolbar-button"
              onClick={() => handleAction('add-complain')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>+</span>
                <span>COMPLAIN</span>
                <i className="dx-icon dx-icon-new"></i>
              </div>
            </button>
          </Item>

          <Item location="before" locateInMenu="auto">
            <button
              className="es-toolbar-button"
              onClick={() => handleAction('add-flight')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>+</span>
                <span>FLIGHT</span>
                <i className="dx-icon dx-icon-new"></i>
              </div>
            </button>
          </Item>

          <Item name="searchPanel" locateInMenu="never" />
        </Toolbar>

        <FilterRow visible />
        {/* REMOVED DevExtreme Selection component */}
        <Paging defaultPageSize={15} />
        <Pager 
          showPageSizeSelector 
          allowedPageSizes={[10, 15, 25, 50, 100]} 
          showInfo 
        />
        
        {/* Default sorting by SNo descending */}
        <Sorting mode="single" />
        <Column dataField="SNo" sortOrder="desc" sortIndex={0} visible={false} />

        {/* Custom Selection Column */}
        <Column
          key="selection-column"
          caption="✓"
          width={50}
          alignment="center"
          headerCellRender={() => <SelectAllHeader />}
          cellRender={(data) => <SelectionColumnCell data={data} />}
          allowFiltering={false}
          allowSorting={false}
        />

        {columns.map((column, index) => (
          <Column
            key={column.dataField || column.caption || `col-${index}`}
            {...column}
            allowFiltering={column.dataField !== undefined}
            allowSorting={column.dataField !== undefined}
            filterOperations={
              column.dataType === "string" 
                ? ["startswith", "contains", "notcontains", "endswith", "=", "<>"] 
                : undefined
            }
          />
        ))}
      </DataGrid>

      <ContextMenu
        ref={contextMenuRef}
        onItemClick={handleContextMenuClick}
        items={contextMenuItems}
        target={gridWrapperRef.current}
        onShowing={(e) => {
          if (!focusedRowKeyRef.current) {
            e.cancel = true;
          }
        }}
        showEvent={null}
        hideOnOutsideClick={true}
        cssClass="applicant-context-menu"
      />

    </div>
    
    {isDocumentSelectorOpen && (
      <DocumentSelector
        isOpen={isDocumentSelectorOpen}
        onClose={() => setIsDocumentSelectorOpen(false)}
        onSubmit={handleDocumentSelection}
      />
    )}

    <InputDialog 
      show={showInputDialog}
      onClose={handleInputDialogClose}
    />
    </>
  );
};

export default ApplicantList;